import { Box, Input } from "../../common/components";
import React from "react";
import { useTranslation } from "react-i18next";
import { TechpackItemQuantity } from "../../types";
import { createStyles, palette } from "../../styles/theme";
import { Typography } from "@mui/material";

const styles = createStyles({
  container: {
    background: palette.white,
    border: `1px solid ${palette.grey.lightGrey}`,
    borderRadius: "8px",
    py: "16px",
    px: "24px",
    overflowX: "auto",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr repeat(10, 1fr)",
    justifyContent: "space-between",
    py: "8px",
    gap: "16px",
    alignItems: "center",
  },
  input: {
    width: "64px",
    p: "8px 12px",
    textAlign: "center",
    minHeight: "40px",
  },
});

interface Props {
  sizesQuantities: TechpackItemQuantity[];
  isHyperCustom: boolean;
  onQtyChange: (type: string, sizeName: string, value: string) => void;
}

const TechpackItemSize = ({
  sizesQuantities,
  isHyperCustom,
  onQtyChange,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.grid,
          gridTemplateColumns: `1fr repeat(${sizesQuantities?.length + 1}, 1fr)`,
        }}
      >
        <Box>&nbsp;</Box>
        {sizesQuantities
          ?.sort((a, b) => a.displayOrder - b.displayOrder)
          .map((size) => (
            <Typography
              key={size.sizeName}
              variant="textRegularBold"
              color={palette.grey.nearBlack}
              sx={{
                justifySelf: "center",
              }}
            >
              {t(`sizes.${size.sizeName}`)}
            </Typography>
          ))}
        <Typography
          variant="textRegularBold"
          sx={{
            justifySelf: "center",
          }}
        >
          Total
        </Typography>

        <Typography variant="textRegularBold" color={palette.grey.nearBlack}>
          {t("techpack.order_quantity")}
        </Typography>
        {sizesQuantities
          ?.sort((a, b) => a.displayOrder - b.displayOrder)
          .map((size) => (
            <Box key={size.sizeName} sx={{ justifySelf: "center" }}>
              <Typography
                variant="actionBigIcon"
                color={palette.grey.nearBlack}
              >
                {size.orderQuantity}
              </Typography>
            </Box>
          ))}
        <Typography
          variant="actionBoldBigIcon"
          color={palette.grey.nearBlack}
          sx={{
            justifySelf: "center",
          }}
        >
          {sizesQuantities?.reduce(
            (accumulator, curr) => accumulator + Number(curr?.orderQuantity),
            0
          )}
        </Typography>

        {isHyperCustom && (
          <>
            <Typography variant="textRegularBold">
              {t("techpack.over_quantity")}
            </Typography>
            {sizesQuantities
              ?.sort((a, b) => a.displayOrder - b.displayOrder)
              .map((size) => (
                <Box
                  key={size.sizeName}
                  sx={{
                    justifySelf: "center",
                  }}
                >
                  <Input
                    defaultValue={
                      size.overQuantity.toString() === "0"
                        ? ""
                        : size.overQuantity
                    }
                    placeholder="0"
                    onChange={(event) =>
                      onQtyChange("OVER", size.sizeName, event.target.value)
                    }
                    sx={styles.input}
                    inputProps={{
                      sx: {
                        textAlign: "center",
                      },
                    }}
                  />
                </Box>
              ))}
            <Typography
              variant="actionBoldBigIcon"
              color={palette.grey.nearBlack}
              sx={{
                justifySelf: "center",
              }}
            >
              {sizesQuantities?.reduce(
                (accumulator, curr) => accumulator + Number(curr?.overQuantity),
                0
              )}
            </Typography>
            <Typography variant="textRegularBold">
              {t("techpack.qc_quantity")}
            </Typography>
            {sizesQuantities
              ?.sort((a, b) => a.displayOrder - b.displayOrder)
              .map((size) => (
                <Box key={size.sizeName} sx={{ justifySelf: "center" }}>
                  <Input
                    defaultValue={
                      size.qcQuantity.toString() === "0" ? "" : size.qcQuantity
                    }
                    placeholder="0"
                    onChange={(event) =>
                      onQtyChange("QC", size.sizeName, event.target.value)
                    }
                    sx={styles.input}
                    inputProps={{
                      sx: {
                        textAlign: "center",
                      },
                    }}
                  />
                </Box>
              ))}
            <Typography
              variant="actionBoldBigIcon"
              color={palette.grey.nearBlack}
              sx={{
                justifySelf: "center",
              }}
            >
              {sizesQuantities?.reduce(
                (accumulator, curr) => accumulator + Number(curr?.qcQuantity),
                0
              )}
            </Typography>
          </>
        )}
      </Box>
      <Box
        sx={{
          ...styles.grid,
          gridTemplateColumns: `1fr repeat(${sizesQuantities?.length + 1}, 1fr)`,
          pt: "16px",
          borderTop: `1px solid ${palette.grey.lightGrey}`,
        }}
      >
        <Typography variant="textRegularBold">{t("techpack.total")}</Typography>
        {sizesQuantities
          ?.sort((a, b) => a.displayOrder - b.displayOrder)
          .map((size) => (
            <Box
              key={size.sizeName}
              sx={{ textAlign: "center", width: "100%" }}
            >
              <Typography
                variant="actionBoldBigIcon"
                color={palette.grey.nearBlack}
              >
                {Number(size.qcQuantity) +
                  Number(size.overQuantity) +
                  Number(size.orderQuantity)}
              </Typography>
            </Box>
          ))}
        <Box sx={{ justifySelf: "center" }}>
          <Typography
            variant="actionBoldBigIcon"
            color={palette.grey.nearBlack}
          >
            {(sizesQuantities?.reduce(
              (accumulator, curr) =>
                accumulator + Number(curr?.orderQuantity || 0),
              0
            ) ?? 0) +
              (sizesQuantities?.reduce(
                (accumulator, curr) =>
                  accumulator + Number(curr?.overQuantity || 0),
                0
              ) ?? 0) +
              (sizesQuantities?.reduce(
                (accumulator, curr) =>
                  accumulator + Number(curr?.qcQuantity || 0),
                0
              ) ?? 0)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TechpackItemSize;
